import shuffle from 'lodash/shuffle';
import partition from 'lodash/partition';
import DEFAULT_COPY from '../constants/defaultCopy';

import {
  PHOTO_UPLOAD_FILE_LIMIT_ERROR_KEY,
  PHOTO_UPLOAD_FILE_MIMES,
  PHOTO_UPLOAD_FILE_SIZE_ERROR_KEY,
  PHOTO_UPLOAD_FILE_TYPE_ERROR_KEY,
  PHOTO_UPLOAD_GENERAL_ERROR_KEY,
  TEN_MB_IN_BYTES,
} from '../constants/photoUpload';
import { receiveUploadErrorMsg, uploadImages } from '../actions/photoUpload';

export const parseReasonJson = (reasonJsonStr) => {
  const reasonJson = reasonJsonStr ? JSON.parse(reasonJsonStr) : {};
  const {
    hidden_fields: hiddenFields = [],
    mandatory_fields: mandatoryFields = [],
    comment_placeholder: commentPlaceholder = '',
    nested_reasons_title: nestedReasonsTitle = '',
    quantity_field: quantityField = null,
    photo_upload_enabled: photoUploadEnabled = false,
    photo_upload_required: photoUploadRequired = false,
    photo_upload_placeholder: photoUploadPlaceholder = '',
    photo_upload_title: photoUploadTitle = '',
    photo_upload_max_files: photoUploadMaxFiles = 1,
  } = reasonJson;
  const commentHidden = hiddenFields.indexOf('comments') > -1;
  const commentRequired = mandatoryFields.indexOf('comments') > -1;

  return {
    commentHidden,
    commentRequired,
    commentPlaceholder,
    nestedReasonsTitle,
    quantityField,
    photoUploadEnabled,
    photoUploadRequired,
    photoUploadPlaceholder,
    photoUploadTitle,
    photoUploadMaxFiles,
  };
};

export const getNestedReasons = (parentReason, randomize) => {
  const { nested, return_reasons: returnReasons } = parentReason;
  if (!nested) return null;
  if (randomize) return shuffle(returnReasons);
  return returnReasons;
};

export const partitionReasonsByProduct = (returnReasonCodes) =>
  returnReasonCodes.reduce(
    (acc, reason) => {
      if (reason.nested) {
        const [exchangeReasons, returnReasons] = partition(reason.return_reasons, {
          reason_type: 'exchange',
        });
        return {
          returnReasons: [
            ...acc.returnReasons,
            ...(returnReasons && returnReasons.length > 0
              ? [{ ...reason, return_reasons: returnReasons }]
              : []),
          ],
          exchangeReasons: [
            ...acc.exchangeReasons,
            ...(exchangeReasons && exchangeReasons.length > 0
              ? [{ ...reason, return_reasons: exchangeReasons }]
              : []),
          ],
        };
      }
      const isExchange = reason.return_reasons[0].reason_type === 'exchange';
      return {
        ...acc,
        ...(isExchange
          ? {
              exchangeReasons: [...acc.exchangeReasons, reason],
            }
          : {
              returnReasons: [...acc.returnReasons, reason],
            }),
      };
    },
    { returnReasons: [], exchangeReasons: [] },
  );

export const handlePhotoUpload = ({
  acceptedFiles,
  rejectedFiles,
  images,
  photoUploadMaxFiles,
  dispatch,
  language,
  languageCode,
}) => {
  const defaultCopy = DEFAULT_COPY[languageCode] || {};
  if (rejectedFiles.some(({ type }) => !PHOTO_UPLOAD_FILE_MIMES.includes(type))) {
    dispatch(
      receiveUploadErrorMsg(
        language[PHOTO_UPLOAD_FILE_TYPE_ERROR_KEY] || defaultCopy[PHOTO_UPLOAD_FILE_TYPE_ERROR_KEY],
      ),
    );
    return;
  }

  if (rejectedFiles.length > 0) {
    dispatch(
      receiveUploadErrorMsg(
        language[PHOTO_UPLOAD_GENERAL_ERROR_KEY] || defaultCopy[PHOTO_UPLOAD_GENERAL_ERROR_KEY],
      ),
    );
    return;
  }

  if (acceptedFiles.some((file) => file.size > TEN_MB_IN_BYTES)) {
    dispatch(
      receiveUploadErrorMsg(
        language[PHOTO_UPLOAD_FILE_SIZE_ERROR_KEY] || defaultCopy[PHOTO_UPLOAD_FILE_SIZE_ERROR_KEY],
      ),
    );
    return;
  }

  if (images.length + acceptedFiles.length > photoUploadMaxFiles) {
    dispatch(
      receiveUploadErrorMsg(
        language[PHOTO_UPLOAD_FILE_LIMIT_ERROR_KEY(photoUploadMaxFiles)] ||
          defaultCopy[PHOTO_UPLOAD_FILE_LIMIT_ERROR_KEY(photoUploadMaxFiles)],
      ),
    );
    return;
  }

  dispatch(uploadImages(acceptedFiles));
};

export const getRetailerReasonCode = ({ selectedReason, selectedNestedReason }) => {
  if (!selectedReason) return undefined;
  return selectedNestedReason
    ? selectedNestedReason.retailer_reason_code
    : selectedReason.return_reasons[0].retailer_reason_code;
};
